// High-level template resolution + send-medium helpers.
//
// These helpers wrap buildTemplateContext plus the appropriate rendering
// function so callers don't have to assemble the context by hand every time.
// resolveTemplateForClient and resolveTemplateForSms remain plain-text; use
// resolveTemplateForHtmlBody when the rendered body will be embedded directly
// into HTML markup.

import {
  buildTemplateContext,
  type TemplateContextAgent,
  type TemplateContextClient,
  type TemplateContextProperty,
  type TemplateContextSystemFields,
} from "./buildTemplateContext";
import {
  renderTemplatePreview,
  renderTemplatePreviewHtml,
  type TemplateContext,
} from "./templateInterpolation";

export interface ResolvedTemplate {
  subject: string;
  body: string;
  context: TemplateContext;
}

export interface ResolveTemplateInput {
  template: { subject: string; body: string };
  client?: TemplateContextClient | null;
  agent?: TemplateContextAgent | null;
  selectedProperties?: ReadonlyArray<TemplateContextProperty>;
  systemFields?: TemplateContextSystemFields;
}

type TemplateRenderer = (
  subject: string,
  body: string,
  context: TemplateContext
) => { subject: string; body: string };

function resolveTemplate(
  {
    template,
    client,
    agent,
    selectedProperties = [],
    systemFields = {},
  }: ResolveTemplateInput,
  render: TemplateRenderer
): ResolvedTemplate {
  const context = buildTemplateContext(
    client,
    agent,
    selectedProperties,
    systemFields
  );
  const { subject, body } = render(template.subject, template.body, context);
  return { subject, body, context };
}

/**
 * Resolve a template's subject/body for a given client + send context.
 *
 * Usage:
 *   const { subject, body } = resolveTemplateForClient({
 *     template,          // fetched from templateService
 *     client,            // the recipient client
 *     agent,             // the sending locator's LocatorProfile
 *     selectedProperties,// what got picked in the send flow, ordered
 *     systemFields: {    // optional overrides
 *       today_date: new Date().toLocaleDateString(...),
 *       unsubscribe_link: maybePrebuiltLink,
 *       budget_range: currentSearchBudgetSummary,
 *     },
 *   });
 *
 * The returned `context` is exposed so callers (preview panes, debug UI)
 * can inspect the exact values used without re-running the builder.
 */
export function resolveTemplateForClient(
  input: ResolveTemplateInput
): ResolvedTemplate {
  return resolveTemplate(input, renderTemplatePreview);
}

/**
 * Explicit plain-text resolver for SMS bodies. Equivalent to
 * resolveTemplateForClient but named for channel clarity.
 */
export function resolveTemplateForSms(
  input: ResolveTemplateInput
): ResolvedTemplate {
  return resolveTemplate(input, renderTemplatePreview);
}

/**
 * HTML-safe resolver for bodies that will be embedded directly into markup.
 * Subject stays plain-text for MIME/header contexts.
 */
export function resolveTemplateForHtmlBody(
  input: ResolveTemplateInput
): ResolvedTemplate {
  return resolveTemplate(input, renderTemplatePreviewHtml);
}

/**
 * Build an `sms:` URL that pre-fills the native SMS app with `body` for
 * the given `phone` number. Used by ClientList's "sent_text" action once
 * the SMS branch lands.
 *
 * Pure, synchronous, no I/O. Returns null when `phone` is empty/whitespace
 * so callers can fall back to a logging-only path.
 *
 * Platform notes:
 * - iOS uses `sms:<num>&body=...` (ampersand separator, not query-string).
 * - Android and cross-platform support `sms:<num>?body=...`.
 * - We emit the `?body=` form because modern iOS (14+) accepts both and
 *   Android rejects the ampersand form. Keep this consistent until a
 *   concrete bug shows up.
 *
 * Length: SMS bodies longer than ~160 chars get split into multiple
 * segments. We do NOT truncate here -- that is a UX decision for the
 * picker/editor layer. The resolver just hands back whatever was
 * interpolated.
 */
export function buildSmsUrl(
  phone: string | null | undefined,
  body: string
): string | null {
  if (!phone) return null;
  const trimmedPhone = phone.trim();
  if (!trimmedPhone) return null;
  // Strip spaces and common punctuation from the number so iOS/Android
  // dialers parse it reliably. Keep "+" for international prefixes.
  const normalizedPhone = trimmedPhone.replace(/[^\d+]/g, "");
  if (!normalizedPhone) return null;
  if (!body) return `sms:${normalizedPhone}`;
  return `sms:${normalizedPhone}?body=${encodeURIComponent(body)}`;
}
